/* eslint-disable max-len */
/* eslint-disable react/no-unescaped-entities */
import React, {
  useState,
  useEffect,
  useRef,
  Fragment,
  useCallback
} from "react";
import PropTypes from "prop-types";
import { useRouteMatch, useHistory } from "react-router-dom";
import { Trans, t } from "@lingui/macro";
import { i18nMark } from "@lingui/react";

import { LazyLoadComponent } from "widgets/lazyloader";
import { ErrorBoundary } from "widgets/layouts";
import { i18n } from "base/i18n";
import { useAccountStore } from "base/stores";
import { Store, useStore } from "base/basestore";
import { throttle, debounce } from "base/utils";
import { BUTTON_TYPES } from "widgets/inputs";
import { Icon } from "widgets/datadisplay";
import { Alert } from "widgets/feedback";
import { Filter } from "kfcomponents/filter";
import { SecondaryTabs, SecondaryTab, SECONDARY_TAB_SIZES } from "widgets/tab";

import { ProcessService } from "../services/process.service.js";
import { createGridDataStore } from "../common/stores/grid.data";
import { useProcessStore } from "../common/stores/process";
import {
  Grid,
  ToolbarAndGrid,
  Toolbar,
  SORTING_HEADER_MENU_LIST,
  HEADER_MENU_LIST
} from "../component";
import { STATUS_LABELS, STATUSES_ARRAY } from "../common/constant";

import styles from "./myitemsandmytasks.css";

import MyItemsEmptyStateIcon from "./icons/MyItemsEmptyState.svg";

const { showConfirm } = Alert;
const ACTION_BUTTONS = [{ Id: "delete", label: i18nMark("Delete") }];
const NON_SORTABLE_COLUMNS = ["_participated_at", "_step_name"];

export function MyItemsWrapper({ uId, ...restProps }) {
  createGridDataStore(uId);
  const [flowModel, setFlowModel] = useState();

  useEffect(
    function getFlowModel() {
      let [getFlowModel] = ProcessService.getFlowModel(restProps.modelId);
      getFlowModel().then((flowModel) => {
        setFlowModel(flowModel);
      });
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return flowModel ? (
    <Store uId={uId}>
      <MyItems {...restProps} flowSchema={flowModel} />
    </Store>
  ) : null;
}
export function MyItems({
  className,
  processInfo,
  queueType: queueTypeProp,
  showTab = true,
  modelId: modelIdProp,
  onRowClick: onRowClickProp,
  preventRedirection,
  flowSchema
}) {
  const { useStateStore: useGridStore } = useStore();

  const gridPageNumber = useGridStore((state) => state.gridPageNumber);
  const gridPageSize = useGridStore((state) => state.gridPageSize);
  const gridData = useGridStore((state) => state.gridData);
  const selectedRowsIds = useGridStore((state) => state.selectedRowsIds);

  let [formData, setFormData] = useState(null);
  const [myItemsCount, setMyItemsCount] = useState(null);
  const match = useRouteMatch();
  const history = useHistory();
  const [queueType, setQueueType] = useState(
    queueTypeProp || match.params["queueType"]
  );
  const [fields, setFields] = useState(null);
  const modelId = modelIdProp || match.params.modelId;
  let accountId = useAccountStore.getState()._id;

  let searchText = useRef("");
  const [showEmptyState, setShowEmptyState] = useState(false);

  useEffect(
    function onQueueTypeChange() {
      if (queueTypeProp) {
        setQueueType(queueTypeProp);
        getStatusData(queueTypeProp);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [queueTypeProp]
  );

  useEffect(function historyListener() {
    const unlisten = history.listen((location) => {
      let url = `/process/${modelId}/myitems`;
      if (location.pathname.includes(url)) {
        setFormData(null);
      }
    });
    return () => {
      unlisten();
    };
  }, []);

  useEffect(function getMyItemsData() {
    useGridStore.getState().reset();
    function getFieldMyitemList() {
      const promise = ProcessService.getFieldMyitemList(modelId);
      promise
        .onResponse((fields) => {
          setFields(fields);
        })
        .onError((err) => console.error("failed to get data", err));
    }
    getMyItemsCount(queueType);
    getQueueData(gridPageNumber, queueType, true);
    getFieldMyitemList();
    const [promise] = ProcessService.getPendingTasksCount({ modelId });
    promise().then((myTasks) => {
      useProcessStore.getState().setMytaskDetails(myTasks, modelId);
    });
    getGridItemsCount(queueType);
    return () => {
      useGridStore.getState().reset();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function getQueueData(pageNum, queue, resetData, pageSize) {
    queue = queue || queueType;
    const promise = ProcessService.getQueueData(
      {
        modelId,
        currentPage: pageNum || gridPageNumber,
        rowsPerPage: pageSize || gridPageSize,
        q: searchText.current
      },
      STATUS_LABELS[queue].key
    );
    promise
      .onResponse((data) => {
        if (resetData) {
          const gridCount = useGridStore.getState().gridCount;
          useGridStore.getState().reset();
          useGridStore.getState().setGridCount(gridCount);
        }
        if (queue === STATUS_LABELS.Participated.key) {
          data.Data = data.Data.map((row) => {
            let tempRowID = row._id.split("/")[0];
            row._id = `${tempRowID}/${row._activity_instance_id}`;
            return row;
          });
          data.Columns.forEach((column) => {
            if (NON_SORTABLE_COLUMNS.includes(column.Id)) {
              column.showHeaderOptions = false;
              column.hideSort = true;
            }
          });
        }
        useGridStore.getState().setGridData(data);
        useGridStore.getState().setGridLoading(false);
        useGridStore.getState().setGridPageSize(data.PageSize || gridPageSize);
      })
      .onError((err) => {
        useGridStore.getState().setGridLoading(false);
      });
  }

  function getMyItemsCount(queueType) {
    const getListCount = ProcessService.getListCount(modelId);

    getListCount
      .onResponse((results) => {
        let myItemsCount = results;
        let totalCount = 0;
        for (const key in myItemsCount) {
          totalCount = totalCount + myItemsCount[key];
        }
        if (totalCount === 0) {
          setShowEmptyState(true);
          setMyItemsCount(null);
        } else {
          setMyItemsCount(myItemsCount);
          setShowEmptyState(false);
        }
      })
      .onError((err) => {
        console.error("failed to get data", err);
      });
  }

  function getGridItemsCount(queue) {
    queue = queue || queueType;
    const promise = ProcessService.getCount(
      { modelId, q: searchText.current },
      queue
    );
    promise
      .onResponse((count) => {
        useGridStore.getState().setGridCount(count.Count);
      })
      .onError((err) => {
        console.error("failed to get data", err);
        useGridStore.getState().setGridLoading(false);
      });
  }

  const getStatusData = useCallback(
    throttle(function (status) {
      useGridStore.getState().reset();
      useGridStore.getState().setGridPageNumber(1);
      useGridStore.getState().setGridLoading(true);
      searchText.current = "";
      getQueueData(1, status, true);
      getGridItemsCount(status);

      if (!preventRedirection) {
        window.history.replaceState(
          null,
          document.title,
          `/process/${modelId}/myitems/${status}`
        );
      }
      setQueueType(status);
    }, 500),
    [gridPageNumber, preventRedirection]
  );

  function onRowClick(row) {
    if (onRowClickProp) {
      onRowClickProp(row, queueType);
    } else {
      const url =
        queueType === STATUS_LABELS.Participated.key
          ? `/process/${modelId}/form/${row._id}`
          : `/process/${modelId}/form/${row._id}/${row._activity_instance_id}`;
      // history.push(url);
      setFormData({
        _id:
          queueType !== STATUS_LABELS.Participated.key
            ? row._id
            : row._id.replace(`/${row._activity_instance_id}`, ""),
        _activity_instance_id: row._activity_instance_id,
        _flow_id: modelId
      });
      window.history.pushState("", "", url);
    }
  }

  function onSearch(value) {
    searchText.current = value;
    getQueueData(gridPageNumber, queueType, true);
    getGridItemsCount(queueType);
  }

  // eslint-disable-next-line kf/hook-func-name
  const delayedSearch = useCallback(
    debounce(function (value) {
      onSearch(value);
    }, 300),
    [queueType]
  );

  const onPreferenceUpdate = ({
    config,
    pageNumber,
    dontCallGetApi,
    isFilterChanged
  }) => {
    const payload = {
      AppId: modelId,
      ConfigJson: config,
      ViewId: STATUS_LABELS[queueType].key,
      ViewType: "WorkflowStep"
    };
    let [savePreference] = ProcessService.updateUserPreference(payload);
    savePreference()
      .then(() => {
        isFilterChanged && getGridItemsCount(queueType);
        !dontCallGetApi &&
          getQueueData(pageNumber, queueType, false, config.PageSize);
      })
      .catch((err) => console.error("Failed to get user preference", err));
  };
  const onFilterChange = (filterData, defaultFilterType) => {
    useGridStore.getState().setGridPageNumber(1);
    useGridStore.getState().setGridLoading(true);
    const ConfigJson = {
      Columns: gridData.Columns,
      Filter: filterData,
      FilterType: defaultFilterType,
      PageSize: gridPageSize,
      Sort: gridData.Sort
    };
    onPreferenceUpdate({
      config: ConfigJson,
      pageNumber: 1,
      isFilterChanged: true
    });
  };
  const onDelete = () => {
    const deleteItems = selectedRowsIds.map((id) => {
      return { _id: id, _is_deleted: true };
    });
    showConfirm({
      title: i18n._(t`Delete item`),
      content: i18n._(
        t`You cannot recover these items after they are deleted. Are you sure you want to 
        delete ${selectedRowsIds.length} items?`
      ),
      okText: i18n._(t`Delete`),
      cancelText: i18n._(t`Cancel`),
      okButtonType: BUTTON_TYPES.ERROR,
      onOK: () => {
        const [deleteData] = ProcessService.deleteData(modelId, deleteItems);
        useGridStore.getState().setGridLoading(true);
        deleteData()
          .then((res) => {
            useGridStore.getState().setSelectedRowsIds([]);
            getQueueData(gridPageNumber, queueType);
            getMyItemsCount(queueType);
            getGridItemsCount(queueType);
          })
          .catch((err) => {
            useGridStore.getState().setGridLoading(false);

            console.error("Failed", err);
          });
      },
      onCancel: () => {}
    });
  };

  const onCloseModal = (event) => {
    let _queueType = queueType;
    const url = `/process/${modelId}/myitems/${_queueType}`;
    setFormData(null);
    window.history.pushState("", "", url);
    if (event !== null) {
      setTimeout(() => {
        getMyItemsCount(queueType);
        getGridItemsCount(_queueType);
        getQueueData(1, _queueType, true);
      }, 300);
    }
  };

  return (
    <ErrorBoundary>
      <Fragment>
        {showEmptyState && !myItemsCount && (
          <div className={styles.emptyStateContainer}>
            <Icon paddingNone={true}>
              <MyItemsEmptyStateIcon />
            </Icon>
            <div className={`h4600 ${styles.emptyStateText}`}>
              <Trans>You haven&apos;t created any items yet</Trans>
            </div>
          </div>
        )}
        {myItemsCount && (
          <div className={`${styles.myItemsContainer} ${className}`}>
            <div className={styles.itemsCountContainer}>
              {showTab && (
                <SecondaryTabs
                  activeTabId={queueType}
                  onChange={getStatusData}
                  maxVisible={6}
                  size={SECONDARY_TAB_SIZES.SMALL}
                >
                  {STATUSES_ARRAY.map((status) => (
                    <SecondaryTab key={status} tabId={status}>
                      <span>{i18n._(STATUS_LABELS[status].label)}</span>
                      {myItemsCount &&
                      myItemsCount[STATUS_LABELS[status].key] ? (
                        <div
                          className={`${styles.countChip} small600 ${
                            status === "Rejected" ? styles.rejected : ""
                          }`}
                        >
                          {myItemsCount &&
                            myItemsCount[STATUS_LABELS[status].key]}
                        </div>
                      ) : null}
                    </SecondaryTab>
                  ))}
                </SecondaryTabs>
              )}
            </div>
            <div className={styles.container}>
              <ToolbarAndGrid>
                <ToolbarAndGrid.Toolbar>
                  <Toolbar
                    title={i18n._(STATUS_LABELS[queueType].label)}
                    fields={fields}
                    selectedFields={gridData && gridData.Columns}
                    onSearch={delayedSearch}
                    onDelete={onDelete}
                    selectedRowsIds={selectedRowsIds}
                    onPreferenceUpdate={onPreferenceUpdate}
                    showEye={queueType !== STATUS_LABELS.Participated.key}
                    showFilterOption={
                      queueType !== STATUS_LABELS.Participated.key
                    }
                    actionButtons={ACTION_BUTTONS}
                    searchValue={searchText.current}
                  />
                </ToolbarAndGrid.Toolbar>
                <ToolbarAndGrid.Grid>
                  <Grid
                    showRowSelection={queueType === STATUS_LABELS.Draft.key}
                    onPreferenceUpdate={onPreferenceUpdate}
                    getGriData={getQueueData}
                    onRowClick={onRowClick}
                    rowSelection={null}
                    headerMenuList={
                      queueType === STATUS_LABELS.Participated.key
                        ? SORTING_HEADER_MENU_LIST
                        : HEADER_MENU_LIST
                    }
                    flowSchema={flowSchema}
                  />
                </ToolbarAndGrid.Grid>
                <ToolbarAndGrid.Filter>
                  <Filter
                    listData={{
                      FilterType: gridData.FilterType,
                      Filter: gridData.Filter
                    }}
                    type="Advance"
                    advanceFilterFieldData={gridData && gridData.Columns}
                    simpleFilterFieldData={gridData && gridData.Columns}
                    onFilterChange={onFilterChange}
                    baseUrl={`/process/2/${accountId}/${modelId}/myitems/${STATUS_LABELS[queueType].key}`}
                    onCloseFilter={() => {
                      useGridStore.getState().setShowFilter(false);
                    }}
                  />
                </ToolbarAndGrid.Filter>
              </ToolbarAndGrid>
            </div>
          </div>
        )}
        {formData && (
          <LazyLoadComponent
            component={() =>
              import(/*webpackPreload:true*/ "../form/formmodal")
            }
            componentName={"FormModalWrapper"}
            instanceId={formData._id}
            activityInstanceId={formData._activity_instance_id}
            storeId={formData._id}
            modelId={formData._flow_id}
            viewType="Modal"
            onModalClose={onCloseModal}
          />
        )}
      </Fragment>
    </ErrorBoundary>
  );
}

MyItems.propTypes = {
  processInfo: PropTypes.object,
  queueType: PropTypes.string,
  showTab: PropTypes.bool,
  modelId: PropTypes.string,
  onRowClick: PropTypes.func,
  preventRedirection: PropTypes.func,
  className: PropTypes.string,
  flowSchema: PropTypes.object
};
MyItemsWrapper.propTypes = {
  uId: PropTypes.string
};
